import type { PressableProps } from "../../primitives";
import type { IconSizeKey } from "../../tokens";
import type { TypographyProps } from "../typography";
import type { ChipSize } from "./types";

type TypographyColor = NonNullable<TypographyProps["color"]>;

export const chipTypographyVariant: Record<ChipSize, "label-sm" | "label-md" | "label-lg"> = {
  sm: "label-sm",
  md: "label-md",
  lg: "label-lg",
};

/**
 * Native tap area: the `hitSlop` above and below that brings each size's mobile
 * height (24, 32, 40) to 44pt, as Button's does; web grows the same way on touch
 * screens (`minTouchTargetClassName`). A caller's `hitSlop` wins.
 *
 * The area is invisible, so it can reach past the chip: rows of chips that wrap
 * with an 8px gap share a few px between rows (12px at `sm`), and on web a chip
 * narrower than 44px also grows sideways into an 8px gap. Accepted
 * (coordinator, 2026-10-28): Chip's one production use, FilterDropdown's
 * trigger on the package list, sits alone in its row.
 */
export const chipHitSlop: Record<ChipSize, NonNullable<PressableProps["hitSlop"]>> = {
  sm: { top: 10, bottom: 10 },
  md: { top: 6, bottom: 6 },
  lg: { top: 2, bottom: 2 },
};

/** Icon size keys: 12/16/24, and on desktop web 16/20/28 through Icon's desktop scale */
export const chipIconSize: Record<ChipSize, IconSizeKey> = {
  sm: "xs",
  md: "sm",
  lg: "lg",
};

export const chipTextColor: Record<
  "default" | "selected",
  { default: TypographyColor; disabled: TypographyColor }
> = {
  default: {
    default: "neutral-foreground-default",
    disabled: "button-disabled-cta-text-disabled",
  },
  selected: {
    default: "button-cta2-default",
    disabled: "button-disabled-cta-text-disabled",
  },
};

export const chipTextStateClass: Partial<Record<"default" | "selected", string>> = {
  selected: "group-hover:text-luce-button-cta2-hover group-active:text-luce-button-cta2-hover",
};

export const chipIconColorClass: Record<
  "default" | "selected",
  { default: string; disabled: string }
> = {
  default: {
    default: "text-luce-neutral-foreground-default",
    disabled: "text-luce-button-disabled-ctaTextDisabled",
  },
  selected: {
    default: "text-luce-button-cta2-default",
    disabled: "text-luce-button-disabled-ctaTextDisabled",
  },
};

export const chipIconStateClass: Partial<Record<"default" | "selected", string>> = {
  selected: "group-hover:text-luce-button-cta2-hover group-active:text-luce-button-cta2-hover",
};

type ChipAccessibilityContext = {
  children: string;
  /** The `selected` prop as given: unset is an action chip, not a toggle that's off */
  selected: boolean | undefined;
  isDisabled: boolean;
} & Pick<PressableProps, "accessibilityLabel" | "accessibilityState">;

export function getChipAccessibilityLabel(context: ChipAccessibilityContext): string {
  return context.accessibilityLabel ?? context.children;
}

/**
 * Native's `selected` follows web's `aria-pressed` (`chip.tsx`): only a chip
 * given `selected` is a toggle, and a chip that discloses something
 * (`expanded`) isn't one. A caller's own `selected` is discarded either way.
 */
export function getChipAccessibilityState(
  context: ChipAccessibilityContext,
): PressableProps["accessibilityState"] {
  const { selected: _callerSelected, ...callerState } = context.accessibilityState ?? {};
  const isToggle = context.selected !== undefined && callerState.expanded == null;
  return {
    ...callerState,
    disabled: context.isDisabled,
    ...(isToggle && { selected: context.selected }),
  };
}
